import {
  PDFDocument,
  pdfjs,
  parsePageSelection,
  formatError,
  renderPdfPageToCanvas,
  PdfPageSelector,
  PdfResultViewer,
  getPdfDocumentParams
} from './pdf-engine.js';
import { byId, isAr, message, formatBytes, saveBlob, trackEvent, showFileAdvisory } from './extra-common.js';

// DOM Elements
const drop = byId('po-dropzone');
const fileInput = byId('po-files');
const workspaceBody = byId('po-workspace-body');
const docTitle = byId('po-doc-title');
const fileSizeEl = byId('po-file-size');
const pageCountHeaderEl = byId('po-header-page-count');
const resetBtn = byId('po-reset-btn');
const addMoreInput = byId('po-add-more');

const poMode = byId('po-mode');
const actionCardBtns = Array.from(document.querySelectorAll('#pdf-organizer-app .pdf-action-card'));

const mergeSection = byId('po-merge-section');
const fileListEl = byId('po-file-list');
const pagesAccordion = byId('po-pages-accordion');
const pagesGrid = byId('po-pages-grid');
const pageCountEl = byId('po-page-count');
const rangeInput = byId('po-pages');
const rangeFrom = byId('po-range-from');
const rangeTo = byId('po-range-to');
const applyRangeBtn = byId('po-range-apply');
const summaryEl = byId('po-selection-summary');
const hintEl = byId('po-mode-hint');

const runBtn = byId('po-run');
const statusEl = byId('po-status');

// Dropzone loading elements
const dropDefault = byId('po-dropzone-default');
const dropLoading = byId('po-dropzone-loading');
const loadingFilename = byId('po-loading-filename');
const progressBar = byId('po-progress-bar');
const loadingStep = byId('po-loading-step');
const loadingPct = byId('po-loading-pct');

// State
let files = [];
let singlePdfDoc = null;
let singlePdfBytes = null;
let currentSinglePageCount = 0;
let pageSelector = null;
let resultViewer = null;
let livePreviewTimer = null;

// Initialize Result Viewer
resultViewer = new PdfResultViewer({
  canvas: byId('po-preview'),
  prevBtn: byId('po-prev-page'),
  nextBtn: byId('po-next-page'),
  currentNumEl: byId('po-curr-page'),
  totalPagesEl: byId('po-total-pages'),
  statsEl: byId('po-preview-stats'),
  loadingEl: byId('po-loading'),
  downloadBtn: null,
  backBtn: null,
  isAr
});

// Initialize Page Selector
pageSelector = new PdfPageSelector({
  gridEl: pagesGrid,
  rangeInput,
  fromInput: rangeFrom,
  toInput: rangeTo,
  applyRangeBtn,
  summaryEl,
  hintEl,
  countEl: pageCountEl,
  presetBtns: Array.from(document.querySelectorAll('#pdf-organizer-app [data-preset]')),
  isAr,
  onChange: () => {
    scheduleLivePreview(150);
  }
});

function scheduleLivePreview(delay = 180) {
  if (livePreviewTimer) clearTimeout(livePreviewTimer);
  livePreviewTimer = setTimeout(async () => {
    if (!files.length) return;
    if (poMode?.value === 'merge' && files.length < 2) return;
    try {
      const { bytes, filename } = await buildOutputPdf();
      await resultViewer.loadBytes(bytes, filename);
    } catch {
      // Silently keep current preview if temporary state is empty
    }
  }, delay);
}

function updateModeUI() {
  const currentMode = poMode?.value || 'extract';
  const isMerge = currentMode === 'merge';

  // Sync Action Card visual buttons
  actionCardBtns.forEach((btn) => {
    const isAct = btn.dataset.action === currentMode;
    btn.classList.toggle('active', isAct);
    btn.setAttribute('aria-checked', String(isAct));
  });

  if (isMerge) {
    if (mergeSection) mergeSection.hidden = false;
    if (pagesAccordion) pagesAccordion.hidden = true;
    if (runBtn) {
      runBtn.textContent = isAr ? 'دمج وتنزيل PDF' : 'Merge & Download PDF';
      runBtn.disabled = files.length < 2;
    }
  } else {
    if (mergeSection) mergeSection.hidden = true;
    if (pagesAccordion) pagesAccordion.hidden = false;
    pageSelector.setMode('extract');
    if (runBtn) {
      runBtn.textContent = isAr ? 'استخراج وتنزيل PDF' : 'Extract & Download PDF';
      runBtn.disabled = !singlePdfBytes;
    }
  }
}

// Action Cards Click Listeners
actionCardBtns.forEach((btn) => {
  btn.addEventListener('click', async () => {
    const act = btn.dataset.action;
    if (poMode) poMode.value = act;
    updateModeUI();
    if (act === 'merge') {
      await renderMergeFilesList();
      scheduleLivePreview(150);
    } else {
      if (files.length) {
        if (!singlePdfBytes) {
          await loadSinglePdf(files[0]);
        } else if (singlePdfDoc) {
          pageSelector.mount(singlePdfDoc, currentSinglePageCount, 'extract');
          scheduleLivePreview(150);
        }
      }
    }
  });
});

if (poMode) {
  poMode.addEventListener('change', async () => {
    const act = poMode.value;
    updateModeUI();
    if (act === 'merge') {
      await renderMergeFilesList();
      scheduleLivePreview(150);
    } else {
      if (files.length) {
        if (!singlePdfBytes) {
          await loadSinglePdf(files[0]);
        } else if (singlePdfDoc) {
          pageSelector.mount(singlePdfDoc, currentSinglePageCount, 'extract');
          scheduleLivePreview(150);
        }
      }
    }
  });
}

function setPdfDropzoneLoading(show, fileName = '', fileSize = 0) {
  if (dropDefault) dropDefault.style.display = show ? 'none' : 'flex';
  if (dropLoading) dropLoading.style.display = show ? 'flex' : 'none';
  if (show && loadingFilename) {
    loadingFilename.textContent = `${fileName} · ${formatBytes(fileSize)}`;
  }
}

function setPdfProgress(pct, statusText) {
  const val = Math.max(0, Math.min(100, Math.round(pct)));
  if (progressBar) progressBar.style.width = `${val}%`;
  if (loadingPct) loadingPct.textContent = `${val}%`;
  if (loadingStep && statusText) loadingStep.textContent = statusText;
}

function readFileWithProgress(file, onProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        const pct = Math.round((e.loaded / e.total) * 100);
        onProgress(pct, e.loaded, e.total);
      }
    };
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('File read failed'));
    reader.readAsArrayBuffer(file);
  });
}

async function renderMergeFilesList() {
  fileListEl.replaceChildren();
  let totalBytes = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    totalBytes += file.size;

    const card = document.createElement('div');
    card.className = 'pdf-merge-file-card';

    const left = document.createElement('div');
    left.className = 'pdf-merge-file-left';

    const canvas = document.createElement('canvas');
    canvas.className = 'pdf-merge-cover-canvas';
    left.appendChild(canvas);

    const info = document.createElement('div');
    info.className = 'pdf-merge-file-info';

    const name = document.createElement('span');
    name.className = 'pdf-merge-file-name';
    name.textContent = `${i + 1}. ${file.name}`;

    const meta = document.createElement('span');
    meta.className = 'pdf-merge-file-meta';
    meta.textContent = `${formatBytes(file.size)}`;

    info.append(name, meta);
    left.appendChild(info);

    const actions = document.createElement('div');
    actions.className = 'pdf-merge-actions';

    const upBtn = document.createElement('button');
    upBtn.type = 'button';
    upBtn.className = 'btn-secondary pdf-order-btn';
    upBtn.textContent = '↑';
    upBtn.title = isAr ? 'تحريك لأعلى' : 'Move up';
    upBtn.disabled = i === 0;
    upBtn.addEventListener('click', () => {
      [files[i], files[i - 1]] = [files[i - 1], files[i]];
      renderMergeFilesList();
      scheduleLivePreview(150);
    });

    const downBtn = document.createElement('button');
    downBtn.type = 'button';
    downBtn.className = 'btn-secondary pdf-order-btn';
    downBtn.textContent = '↓';
    downBtn.title = isAr ? 'تحريك لأسفل' : 'Move down';
    downBtn.disabled = i === files.length - 1;
    downBtn.addEventListener('click', () => {
      [files[i], files[i + 1]] = [files[i + 1], files[i]];
      renderMergeFilesList();
      scheduleLivePreview(150);
    });

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn-secondary pdf-remove-file-btn';
    removeBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    removeBtn.title = isAr ? 'إزالة الملف' : 'Remove file';
    removeBtn.addEventListener('click', () => {
      files.splice(i, 1);
      if (!files.length) {
        resetWorkspace();
      } else {
        renderMergeFilesList();
        scheduleLivePreview(150);
      }
    });

    actions.append(upBtn, downBtn, removeBtn);
    card.append(left, actions);
    fileListEl.appendChild(card);

    // Cover render in background
    (async () => {
      try {
        const doc = await pdfjs.getDocument(getPdfDocumentParams({ data: new Uint8Array(await file.arrayBuffer()) })).promise;
        meta.textContent = `${formatBytes(file.size)} · ${doc.numPages} ${isAr ? 'صفحة' : 'pages'}`;
        const page = await doc.getPage(1);
        await renderPdfPageToCanvas(page, canvas, 60, 0);
        await doc.destroy();
      } catch {
        // Fallback silently
      }
    })();
  }

  if (fileSizeEl) fileSizeEl.textContent = formatBytes(totalBytes);
  if (docTitle) docTitle.textContent = isAr ? `${files.length} ملفات للدمج` : `${files.length} files to merge`;
  if (pageCountHeaderEl) pageCountHeaderEl.textContent = isAr ? `${files.length} ملفات` : `${files.length} files`;
  if (runBtn) runBtn.disabled = files.length < 2;
  if (files.length >= 2) scheduleLivePreview(200);

  if (files.length >= 15) {
    showFileAdvisory(
      byId('pdf-organizer-app'),
      isAr
        ? `عدد ملفات كبير (${files.length} ملفات) — المعالجة تتم محلياً في متصفحك.`
        : `Large batch (${files.length} files) — processing locally in your browser.`
    );
  } else {
    showFileAdvisory(byId('pdf-organizer-app'), '', false);
  }
}

async function loadSinglePdf(file) {
  setPdfDropzoneLoading(true, file.name, file.size);
  setPdfProgress(15, isAr ? 'جارٍ قراءة بيانات المستند من جهازك...' : 'Reading document bytes from storage...');

  try {
    const buffer = await readFileWithProgress(file, (pct, loaded, total) => {
      const scaled = Math.round(15 + pct * 0.45);
      const text = isAr 
        ? `جارٍ قراءة المستند (${formatBytes(loaded)} من ${formatBytes(total)})...`
        : `Reading document (${formatBytes(loaded)} of ${formatBytes(total)})...`;
      setPdfProgress(scaled, text);
    });

    setPdfProgress(65, isAr ? 'جارٍ تحليل هيكل PDF واستخراج الصفحات...' : 'Parsing PDF structure and pages...');
    singlePdfBytes = new Uint8Array(buffer);

    if (singlePdfDoc) {
      try { await singlePdfDoc.destroy(); } catch {}
      singlePdfDoc = null;
    }

    singlePdfDoc = await pdfjs.getDocument(getPdfDocumentParams({ data: singlePdfBytes.slice(0) })).promise;
    currentSinglePageCount = singlePdfDoc.numPages;

    setPdfProgress(85, isAr ? `جارٍ تجهيز بطاقات ${currentSinglePageCount} صفحة...` : `Generating cards for ${currentSinglePageCount} pages...`);

    if (docTitle) docTitle.textContent = file.name;
    if (fileSizeEl) fileSizeEl.textContent = formatBytes(file.size);
    if (pageCountHeaderEl) pageCountHeaderEl.textContent = isAr ? `${currentSinglePageCount} صفحة` : `${currentSinglePageCount} pages`;

    if (poMode) poMode.value = 'extract';
    updateModeUI();

    pageSelector.mount(singlePdfDoc, currentSinglePageCount, 'extract');
    if (runBtn) runBtn.disabled = false;

    setPdfProgress(100, isAr ? 'جاهز!' : 'Ready!');
    await new Promise((r) => setTimeout(r, 150));
    setPdfDropzoneLoading(false);

    if (drop) drop.hidden = true;
    if (workspaceBody) workspaceBody.hidden = false;

    message('po-status', isAr ? `تم تحميل ${currentSinglePageCount} صفحة. المعاينة المباشرة جاهزة بالأسفل.` : `${currentSinglePageCount} pages loaded. Live preview ready below.`, 'success');
    scheduleLivePreview(60);
  } catch (err) {
    setPdfDropzoneLoading(false);
    message('po-status', formatError(err, isAr), 'error');
  }
}

async function handleSelectedFiles(incomingList) {
  const chosen = Array.from(incomingList).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
  if (!chosen.length) {
    message('po-status', isAr ? 'يرجى اختيار ملفات PDF صالحة.' : 'Please choose valid PDF files.', 'error');
    return;
  }

  files = chosen;
  trackEvent('tool_started', 'pdf-organizer');

  if (files.length > 1) {
    if (poMode) poMode.value = 'merge';
    updateModeUI();
    if (drop) drop.hidden = true;
    if (workspaceBody) workspaceBody.hidden = false;
    await renderMergeFilesList();
    message('po-status', isAr ? `تم تحميل ${files.length} ملفات. يمكنك تعديل ترتيبها بالأسهم.` : `Loaded ${files.length} files. Reorder them with arrows.`, 'success');
  } else {
    if (poMode) poMode.value = 'extract';
    await loadSinglePdf(files[0]);
  }
}

function resetWorkspace() {
  files = [];
  singlePdfBytes = null;
  if (singlePdfDoc) {
    try { singlePdfDoc.destroy(); } catch {}
    singlePdfDoc = null;
  }
  if (fileInput) fileInput.value = '';
  if (addMoreInput) addMoreInput.value = '';
  pageSelector.destroy();
  resultViewer.destroy();
  if (workspaceBody) workspaceBody.hidden = true;
  if (drop) drop.hidden = false;
  setPdfDropzoneLoading(false);
  message('po-status', '');
}

if (resetBtn) resetBtn.addEventListener('click', resetWorkspace);

if (addMoreInput) {
  const addMoreDropzone = addMoreInput.closest('.kz-mini-dropzone, .dropzone');
  const appendExtraFiles = (fileList) => {
    const extra = Array.from(fileList || []).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (!extra.length) return;
    files = [...files, ...extra];
    renderMergeFilesList();
  };

  addMoreInput.addEventListener('change', () => {
    appendExtraFiles(addMoreInput.files);
    addMoreInput.value = '';
  });

  if (addMoreDropzone) {
    addMoreDropzone.addEventListener('dragover', (event) => {
      event.preventDefault();
      addMoreDropzone.classList.add('dragover');
    });
    addMoreDropzone.addEventListener('dragleave', () => addMoreDropzone.classList.remove('dragover'));
    addMoreDropzone.addEventListener('drop', (event) => {
      event.preventDefault();
      addMoreDropzone.classList.remove('dragover');
      if (event.dataTransfer && event.dataTransfer.files) {
        appendExtraFiles(event.dataTransfer.files);
      }
    });
    addMoreDropzone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        addMoreInput.click();
      }
    });
  }
}

if (drop && fileInput) {
  drop.addEventListener('click', (event) => {
    if (!event.target.closest('button') && !event.target.closest('input')) fileInput.click();
  });
  drop.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); fileInput.click(); }
  });
  drop.addEventListener('dragover', (event) => {
    event.preventDefault();
    drop.classList.add('dragover');
  });
  drop.addEventListener('dragleave', () => drop.classList.remove('dragover'));
  drop.addEventListener('drop', (event) => {
    event.preventDefault();
    drop.classList.remove('dragover');
    handleSelectedFiles(event.dataTransfer.files);
  });
  fileInput.addEventListener('change', () => handleSelectedFiles(fileInput.files));
}

async function buildOutputPdf() {
  if (!files.length) throw new Error(isAr ? 'لم يتم اختيار ملفات.' : 'No files selected.');

  if (poMode.value === 'merge') {
    if (files.length < 2) throw new Error(isAr ? 'اختر ملفين على الأقل للدمج.' : 'Select at least 2 files to merge.');
    const merged = await PDFDocument.create();
    for (const file of files) {
      const srcDoc = await PDFDocument.load(await file.arrayBuffer());
      const copied = await merged.copyPages(srcDoc, srcDoc.getPageIndices());
      copied.forEach((p) => merged.addPage(p));
    }
    return { bytes: await merged.save(), filename: 'merged-documents.pdf' };
  }

  // Extract
  if (!singlePdfBytes) throw new Error(isAr ? 'لم يتم تحميل المستند.' : 'Document not loaded.');
  const srcDoc = await PDFDocument.load(singlePdfBytes);
  const selected = pageSelector.getSelectedIndices();
  if (!selected.length) throw new Error(isAr ? 'حدد صفحة واحدة على الأقل للاستخراج.' : 'Select at least one page to extract.');

  const outDoc = await PDFDocument.create();
  const copied = await outDoc.copyPages(srcDoc, selected);
  copied.forEach((p) => outDoc.addPage(p));
  return { bytes: await outDoc.save(), filename: 'organized-document.pdf' };
}

runBtn.addEventListener('click', async () => {
  if (!files.length) return;
  runBtn.disabled = true;
  message('po-status', isAr ? 'جارٍ معالجة المستند محليًا وتنزيله…' : 'Processing document and downloading…');
  try {
    const { bytes, filename } = await buildOutputPdf();
    const blob = new Blob([bytes], { type: 'application/pdf' });
    saveBlob(blob, filename);
    message('po-status', isAr ? `تم تجهيز الملف بنجاح (${formatBytes(blob.size)})` : `PDF ready (${formatBytes(blob.size)})`, 'success');
    trackEvent('tool_completed', 'pdf-organizer');
  } catch (error) {
    message('po-status', formatError(error, isAr), 'error');
    trackEvent('tool_failed', 'pdf-organizer');
  } finally {
    runBtn.disabled = false;
  }
});
